import React, { useCallback, useContext, useEffect, useState } from "react";

import closeCircle from "@iconify/icons-mdi/close-circle";
import incognitoIcon from "@iconify/icons-mdi/incognito";
import { Icon } from "@iconify/react/dist/offline";
import { useSnackbar } from "notistack";
import { useLocation } from "react-router-dom";

import backendClient from "src/api";
import { Button } from "src/components/ui/button";
import { UserContext } from "src/contexts/UserContext";
import authService from "src/services/authService";

const DD_APP_FRONTEND_APPLICATION_ID = "7897a9ed-18e1-465e-a749-4c8d215b3a90";

const ImpersonationIndicator = () => {
  const { user } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();

  const initialPosition = { x: window.innerWidth - 320, y: 16 };
  const [position, setPosition] = useState(initialPosition);
  const [dragging, setDragging] = useState(false);
  const [rel, setRel] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest(".cancel-button")) return;
    e.preventDefault();
    setDragging(true);
    setRel({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragging) return;
      const maxX = window.innerWidth - 300;
      const maxY = window.innerHeight - 60;
      setPosition({
        x: Math.min(Math.max(0, e.clientX - rel.x), maxX),
        y: Math.min(Math.max(0, e.clientY - rel.y), maxY),
      });
    };

    const handleMouseUp = () => setDragging(false);

    const handleResize = () => {
      const maxX = window.innerWidth - 300;
      const maxY = window.innerHeight - 60;
      setPosition((prev) => ({
        x: Math.min(prev.x, maxX),
        y: Math.min(prev.y, maxY),
      }));
    };

    if (dragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }
    window.addEventListener("resize", handleResize);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("resize", handleResize);
    };
  }, [dragging, rel]);

  // Reset position on route change
  useEffect(() => {
    setPosition(initialPosition);
  }, [location.pathname]); // eslint-disable-line

  const handleStopImpersonation = useCallback(async () => {
    try {
      const response = await backendClient.default.cancelImpersonateUser();
      authService.handleAuthentication(response);
      if (location.pathname.startsWith("/clusters/")) {
        window.location.reload();
      } else {
        window.location.href = "/";
      }
    } catch (error: any) {
      const errorMessage =
        error?.body?.detail ||
        error?.response?.data?.detail ||
        error?.message ||
        "An unknown error occurred";
      enqueueSnackbar(`Failed to stop impersonation: ${errorMessage}`, {
        variant: "error",
        autoHideDuration: 5000,
      });
    }
  }, [enqueueSnackbar, location.pathname]);

  const handleViewSessionClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!user) return;
      const baseUrl = "https://app.datadoghq.com/rum/replay/search";
      const query = `${user.email} @application.id:${DD_APP_FRONTEND_APPLICATION_ID} @session.type:user @type:session @session.has_replay:true`;
      window.open(
        `${baseUrl}?query=${encodeURIComponent(
          query
        )}&fromUser=false&replay_tab=all`,
        "_blank"
      );
    },
    [user]
  );

  if (!user?.impersonatorId) return null;

  return (
    <div
      className="fixed flex items-center select-none"
      style={{
        top: position.y,
        left: position.x,
        transition: dragging ? "none" : "all 0.2s ease-out",
        zIndex: 1301,
        gap: 6,
        padding: "6px 10px 6px 8px",
        background: "hsl(var(--warning) / 0.15)",
        border: "1px solid hsl(var(--warning) / 0.25)",
        borderRadius: 10,
        cursor: "move",
        pointerEvents: "auto",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        fontFamily: '"DM Sans", system-ui, sans-serif',
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={handleMouseDown}
    >
      {/* Datadog session link */}
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={handleViewSessionClick}
        className="flex-shrink-0 w-[26px] h-[26px] rounded-md bg-[hsl(var(--warning)/0.12)] border border-[hsl(var(--warning)/0.2)] hover:bg-[hsl(var(--warning)/0.2)]"
      >
        <Icon
          icon={incognitoIcon}
          width={14}
          height={14}
          style={{ color: "hsl(var(--warning))" }}
        />
      </Button>

      {/* User info */}
      <div style={{ margin: "0 4px", minWidth: 0 }}>
        <div
          className="overflow-hidden text-ellipsis whitespace-nowrap"
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: "hsl(var(--warning))",
            lineHeight: 1.3,
          }}
        >
          Impersonating: {user.fullName}
        </div>
        <div
          className="overflow-hidden text-ellipsis whitespace-nowrap"
          style={{
            fontSize: 10.5,
            color: "hsl(var(--warning) / 0.7)",
            lineHeight: 1.3,
          }}
        >
          {user.email}
        </div>
      </div>

      {/* Stop button */}
      <Button
        variant="ghost"
        size="icon-sm"
        className="cancel-button flex-shrink-0 w-[22px] h-[22px] rounded-[5px] ml-0.5"
        onClick={(e) => {
          e.stopPropagation();
          handleStopImpersonation();
        }}
      >
        <Icon
          icon={closeCircle}
          width={16}
          height={16}
          style={{ color: "hsl(var(--warning))" }}
        />
      </Button>
    </div>
  );
};

export default ImpersonationIndicator;
